/* eslint-disable no-underscore-dangle */
import * as R from "ramda";
import { all, call, fork, put, select, takeEvery } from "redux-saga/effects";
import { actions, getters } from "Portal/NewPortal/FollowupForm/model";
import {
  getContactDataFields,
  getContactValues
} from "Portal/NewPortal/FollowupForm/selectors";
import {
  getCredentials,
  user as getUser,
  userId as getUserId
} from "redux/modules/user/selectors";
import { registerError } from "redux/modules/errors/actions";
import Api from "Portal/NewPortal/FollowupForm/api";
import Helpers from "utils/Global/Helpers";
import { push } from "react-router-redux";
import { apiCall } from "App/Data/sagas";
import { debounce } from "utils/General/sagas";

const getParams = function*() {
  const formData = yield select(getters.formData);
  const intakeId = yield select(getters.intakeId);
  const userId = yield select(getUserId);

  return {
    userId,
    eventId: formData.event_id,
    eventUUID: formData.event_uuid,
    intakeId
  };
};

const init = function*({
  payload: {
    formId,
    eventSlug,
    submissionId,
    postLogin,
    returnTo,
    impersonateContactId,
    impersonateAccountId
  }
}) {
  const credentials = yield select(getCredentials);
  const userId = yield select(getUserId);
  const user = yield select(getUser);
  yield put(actions.setLoading(true));
  yield put(actions.setReturnTo(returnTo));
  yield put(actions.setImpersonateContactId(impersonateContactId));
  yield put(actions.setImpersonateAccountId(impersonateAccountId));

  try {
    yield put(actions.setIntakeId(formId));

    const { payload } = yield call(Api.getForm, {
      credentials,
      formId,
      query: {
        submissionId,
        impersonateContactId,
        impersonateAccountId
      }
    });

    const userEmail = user?.email ?? null;

    if (submissionId && !userId) {
      window.location = `/login?returnTo=/followup-forms/${eventSlug}/${formId}/submissions/${submissionId}`;
      return false;
    }

    yield put(
      actions.seedFormData({
        submissionId,
        userEmail,
        formPayload: payload,
        isLoggedIn: Boolean(userId),
        loggedInLabel:
          user.fname && user.lname.length
            ? `${user.fname} ${user.lname} (${user.email})`
            : user.email,
        selectedContactId: null,
        contactValues: payload.contact_values,
        eventName: payload.event_name,
        eventSlug: payload.event_slug,
        styles: payload.styles,
        cssCode: payload.css_code,
        accentColor: payload.accent_color,
        backgroundImageUrl: payload.background_image_url,
        logoImageUrl: payload.logo_image_url,
        currentContactEmail: payload.current_contact_email
      })
    );

    if (postLogin) {
      yield put(actions.setActiveIndex(1));
    }
  } catch (e) {
    yield all([
      put(
        registerError([
          {
            system: e,
            user: "An error occurred getting intake form data"
          }
        ])
      )
    ]);
  } finally {
    yield put(actions.setLoading(false));
  }
  return true;
};

const saveValues = function*() {
  const { eventId, eventUUID, intakeId } = yield call(getParams);
  const returnTo = yield select(getters.returnTo);
  const submissionId = yield select(getters.submissionId);
  const contactValues = yield select(getContactValues);
  const contactFields = yield select(getContactDataFields);
  const formData = yield select(getters.formData);
  const credentials = yield select(getCredentials);
  const impersonateContactId = yield select(getters.impersonateContactId);
  const impersonateAccountId = yield select(getters.impersonateAccountId);

  yield put(actions.setError(""));
  yield put(actions.setSaving(true));

  // create submission
  try {
    yield call(Api.createSubmission, credentials, intakeId, {
      eventId,
      submissionId,
      formId: intakeId,
      contactValues: R.pick(contactFields.map(f => f.id))(contactValues),
      impersonateContactId,
      impersonateAccountId
    });

    if (returnTo) {
      window.location = returnTo;
    } else {
      window.location = `/portals/${formData.event_slug}/${eventUUID}`;
    }
  } catch (e) {
    yield put(actions.setSaving(false));
    yield put(
      registerError([
        {
          system: e,
          user: "An error occurred saving session"
        }
      ])
    );
  }
};

const nextPage = function*() {
  const activeIndex = yield select(getters.activeIndex);

  if (activeIndex < 2) {
    document.body.scrollTop = document.documentElement.scrollTop = 0;
    yield put(actions.setActiveIndex(activeIndex + 1));
  } else {
    const verifyingEmail = yield select(getters.verifyingEmail);
    if (verifyingEmail) {
      return;
    }

    yield call(saveValues);
  }
};

const backPage = function*() {
  const activeIndex = yield select(getters.activeIndex);

  if (activeIndex > 0) {
    document.body.scrollTop = document.documentElement.scrollTop = 0;
    yield put(actions.setActiveIndex(activeIndex - 1));
  }
};

const goToLoginPage = function*() {
  const formData = yield select(getters.formData);
  window.location = `/portal-login/${formData.event_slug}/${formData.event_uuid}?returnTo=/followup-forms/${formData.form_slug}/${formData.form_id}`;
};

const handleLogout = function*() {
  const formData = yield select(getters.formData);
  window.location = `/logout?returnTo=/followup-forms/${formData.form_slug}/${formData.form_id}`;
};

const setEmailError = function*(emailError) {
  yield all([
    put(actions.setEmailError(emailError)),
    put(actions.setError(emailError))
  ]);
  return !emailError.length;
};

const checkIfContactExists = function*(email) {
  const { eventUUID } = yield call(getParams);
  try {
    yield call(apiCall, {
      method: "post",
      url: `/portal/event/${eventUUID}/contact-exists`,
      data: {
        email
      }
    });

    // If the contact exists, it will throw an error
    return false;
  } catch (error) {
    const message = R.path(["body", "message"], error);

    if (message && message.length) {
      return message;
    }
    throw error;
  }
};

const verifyContactEmail = function*({ payload: email }) {
  yield put(actions.setVerifyingEmail(true));

  const cleanedEmail = email ? email.trim().toLowerCase() : null;
  if (!cleanedEmail || !Helpers.isValidEmail(cleanedEmail)) {
    yield put(actions.setVerifyingEmail(false));
    return yield setEmailError("Please enter a valid email address");
  }

  let emailError = "";
  const eventEnableUniqueContacts = yield select(
    getters.eventEnableUniqueContacts
  );
  const currentContactEmail = yield select(getters.currentContactEmail);
  const isEmailTheSameAsPrevious = cleanedEmail === currentContactEmail;

  try {
    if (eventEnableUniqueContacts && !isEmailTheSameAsPrevious) {
      const emailExists = yield call(checkIfContactExists, cleanedEmail);

      if (emailExists) {
        emailError =
          "This email is already associated with a contact profile. Please enter a different email.";
      } else {
        emailError = "";
      }
    }
  } catch (error) {
    emailError = R.or(
      R.path(["error", "message"], error),
      R.prop("message", error),
      ""
    );
  }

  yield put(actions.setSaving(false));
  yield put(actions.setVerifyingEmail(false));

  return yield setEmailError(emailError);
};

const goToPortalHome = function*({ payload: formData }) {
  yield put(push(`/portals/${formData.event_slug}/${formData.event_uuid}`));
};

const watchVerifyEmail = debounce(
  actions.verifyEmail.type,
  verifyContactEmail,
  300
);

const watchInit = function*() {
  yield takeEvery(actions.init.type, init);
};

const watchLogout = function*() {
  yield takeEvery(actions.handleLogout.type, handleLogout);
};

const watchNextPage = function*() {
  yield takeEvery(actions.nextPage.type, nextPage);
};

const watchBackPage = function*() {
  yield takeEvery(actions.backPage.type, backPage);
};

const watchGoToLoginPage = function*() {
  yield takeEvery(actions.goToLoginPage.type, goToLoginPage);
};

const watchGoToPortalHome = function*() {
  yield takeEvery(actions.goToPortalHome.type, goToPortalHome);
};

const rootSaga = function*() {
  yield all([
    fork(watchInit),
    fork(watchNextPage),
    fork(watchGoToLoginPage),
    fork(watchLogout),
    fork(watchBackPage),
    fork(watchVerifyEmail),
    fork(watchGoToPortalHome)
  ]);
};

export default rootSaga;
